// src/pages/VerifyOtp.tsx
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RULES } from '@sdg/shared'
import {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardHeader,
  CardTitle,
  Note,
} from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Field, Input } from '../components/ui/input'
import { cn } from '../lib/cn'
import { ApiError, post, rateLimitMessage } from '../lib/api'
import { clearPending, getPending, savePending, saveSession } from '../lib/session'
import AuthLayout from '../components/AuthLayout'

interface VerifiedResponse {
  status: 'verified'
  returning: boolean
  sessionToken: string
  expiresAt: string
}

/** "0:45", as printed on slide 9. */
function mmss(seconds: number) {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

const cardClass =
  'w-full max-w-sm rounded-2xl border border-white bg-white/90 text-slate-900 shadow-xl shadow-blue-900/10'
const eyebrowClass = 'text-[11px] font-bold uppercase tracking-widest text-orange-600'
const titleClass = 'text-2xl font-bold text-[#0B1E3F]'
const descClass = 'text-sm text-slate-500'
const primaryBtn =
  'h-11 rounded-lg bg-orange-500 py-0 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-600 disabled:opacity-60'
const linkBtn = 'h-auto py-1 text-[13px] font-semibold text-orange-600 hover:underline'

export default function VerifyOtp() {
  const navigate = useNavigate()

  /**
   * Held in state, read from storage once on mount.
   *
   * Reading it on every render raced the success path: verifying clears the
   * pending registration, the `setSubmitting(false)` that follows re-renders
   * this component, `pending` came back null, and the guard below fired
   * `navigate('/')` — overriding the navigation to the pass a moment earlier.
   * The student landed back on the registration form with a valid session.
   */
  const [pending, setPending] = useState(() => getPending())

  const [digits, setDigits] = useState<string[]>(Array(RULES.otp.length).fill(''))
  const [cooldown, setCooldown] = useState(pending?.resendInSeconds ?? 0)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [changingEmail, setChangingEmail] = useState(false)
  const [newEmail, setNewEmail] = useState('')

  const inputs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    if (!pending) navigate('/', { replace: true })
  }, [pending, navigate])

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000)
    return () => clearInterval(timer)
  }, [cooldown])

  useEffect(() => {
    inputs.current[0]?.focus()
  }, [])

  if (!pending) return null

  const code = digits.join('')

  function setDigit(index: number, value: string) {
    const clean = value.replace(/\D/g, '')
    setError(null)

    if (clean.length > 1) {
      // Pasted or autofilled: spread the digits across the boxes.
      const next = [...digits]
      for (let i = 0; i < clean.length && index + i < RULES.otp.length; i++) {
        next[index + i] = clean[i]
      }
      setDigits(next)
      inputs.current[Math.min(index + clean.length, RULES.otp.length - 1)]?.focus()
      return
    }

    const next = [...digits]
    next[index] = clean
    setDigits(next)
    if (clean && index < RULES.otp.length - 1) inputs.current[index + 1]?.focus()
  }

  function onKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) inputs.current[index - 1]?.focus()
    if (e.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus()
    if (e.key === 'ArrowRight' && index < RULES.otp.length - 1) inputs.current[index + 1]?.focus()
  }

  async function verify(e?: React.FormEvent) {
    e?.preventDefault()
    if (code.length !== RULES.otp.length || submitting) return

    setSubmitting(true)
    setError(null)

    try {
      const res = await post<VerifiedResponse>('/api/student/verify-otp', {
        email: pending!.email,
        code,
      })
      saveSession(res.sessionToken, res.expiresAt)
      clearPending()
      // The pass page shows the celebration over the pass itself, so the
      // student sees what they have earned behind it.
      navigate('/pass', { replace: true, state: { celebrate: true, returning: res.returning } })
    } catch (err) {
      const apiErr = err as ApiError
      const detail = apiErr.detail as { attemptsLeft?: number } | undefined

      if (apiErr.message === 'invalid_code') {
        const left = detail?.attemptsLeft ?? 0
        setError(`Incorrect code. ${left} attempt${left === 1 ? '' : 's'} left.`)
      } else if (apiErr.message === 'code_expired') {
        setError('That code has expired. Request a new one below.')
        setCooldown(0)
      } else if (apiErr.message === 'too_many_attempts') {
        setError('Too many attempts — this code is cancelled. Request a new one below.')
        setCooldown(0)
      } else {
        setError(rateLimitMessage(err) ?? 'Something went wrong. Please try again.')
      }
      setDigits(Array(RULES.otp.length).fill(''))
      inputs.current[0]?.focus()
    } finally {
      setSubmitting(false)
    }
  }

  async function resend() {
    setError(null)
    try {
      const res = await post<{ maskedEmail: string; resendInSeconds: number }>(
        '/api/student/resend-otp',
        { email: pending!.email },
      )
      const next = {
        ...pending!,
        maskedEmail: res.maskedEmail,
        resendInSeconds: res.resendInSeconds,
      }
      savePending(next)
      setPending(next)
      setCooldown(res.resendInSeconds)
      setDigits(Array(RULES.otp.length).fill(''))
      inputs.current[0]?.focus()
    } catch (err) {
      const apiErr = err as ApiError
      const detail = apiErr.detail as { retryInSeconds?: number } | undefined
      if (apiErr.message === 'too_soon' && detail?.retryInSeconds) setCooldown(detail.retryInSeconds)
      else setError('Could not resend the code. Please try again shortly.')
    }
  }

  async function changeEmail(e: React.FormEvent) {
    e.preventDefault()
    const next = newEmail.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
      setError('Enter a valid e-mail address.')
      return
    }
    try {
      const res = await post<{ maskedEmail: string; resendInSeconds: number }>(
        '/api/student/change-email',
        { email: pending!.email, newEmail: next },
      )
      const updated = {
        ...pending!,
        email: next,
        maskedEmail: res.maskedEmail,
        resendInSeconds: res.resendInSeconds,
      }
      savePending(updated)
      setPending(updated)
      setCooldown(res.resendInSeconds)
      setChangingEmail(false)
      setNewEmail('')
      setDigits(Array(RULES.otp.length).fill(''))
      setError(null)
    } catch (err) {
      setError(
        (err as ApiError).message === 'email_taken'
          ? 'That address is already registered to another student.'
          : 'Could not update that e-mail. Please try again.',
      )
    }
  }

  /* ---------- Change e-mail view ---------- */
  if (changingEmail) {
    return (
      <AuthLayout>
        <Card className={cardClass}>
          <CardHeader className="space-y-1 px-6 pb-2 pt-6">
            <CardEyebrow className={eyebrowClass}>E-mail verification</CardEyebrow>
            <CardTitle className={titleClass}>Change your e-mail</CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6 pt-3">
            <form onSubmit={changeEmail} className="space-y-4">
              <Field label="New e-mail address" htmlFor="new-email">
                <Input
                  id="new-email"
                  type="email"
                  inputMode="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoFocus
                  className="h-11 rounded-lg border-slate-200 bg-white text-sm focus:border-blue-500 focus:ring-blue-500/20"
                />
              </Field>

              {error && (
                <Note tone="error" className="rounded-lg text-xs">
                  {error}
                </Note>
              )}

              <Button type="submit" block className={primaryBtn}>
                Send new code
              </Button>

              <div className="text-center">
                <Button
                  type="button"
                  variant="link"
                  className="h-auto py-1 text-[13px] font-normal text-slate-500"
                  onClick={() => {
                    setChangingEmail(false)
                    setError(null)
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </AuthLayout>
    )
  }

  /* ---------- OTP view ---------- */
  return (
    <AuthLayout>
      <Card className={cardClass}>
        <CardHeader className="space-y-1 px-6 pb-2 pt-6">
          <CardEyebrow className={eyebrowClass}>E-mail verification</CardEyebrow>
          <CardTitle className={titleClass}>
            {pending.accountExists ? 'Welcome back' : 'Verify your e-mail'}
          </CardTitle>
          <CardDescription className={descClass}>
            We sent a {RULES.otp.length}-digit code to{' '}
            <b className="font-semibold text-[#0B1E3F]">{pending.maskedEmail}</b>. It expires in{' '}
            {RULES.otp.ttlMinutes} minutes.
          </CardDescription>
        </CardHeader>

        <CardContent className="px-6 pb-6 pt-3">
          {pending.accountExists && (
            <Note className="mb-4 rounded-lg border-blue-100 bg-blue-50/80 text-xs text-slate-600">
              Enter the code to reopen the pass you already have — no second pass is issued.
            </Note>
          )}

          <form onSubmit={verify}>
            <div className="flex justify-center gap-1.5 sm:gap-2">
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    inputs.current[i] = el
                  }}
                  value={d}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onKeyDown={(e) => onKeyDown(i, e)}
                  inputMode="numeric"
                  // iOS and Android offer the code straight from the e-mail.
                  autoComplete={i === 0 ? 'one-time-code' : 'off'}
                  maxLength={RULES.otp.length}
                  aria-label={`Digit ${i + 1}`}
                  className={cn(
                    'h-12 w-full max-w-[46px] rounded-lg border text-center text-xl font-bold text-[#0B1E3F]',
                    'transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500/20',
                    d ? 'border-orange-400 bg-white' : 'border-slate-200 bg-slate-50',
                    'focus:border-orange-500 focus:bg-white',
                  )}
                />
              ))}
            </div>

            {error && (
              <Note tone="error" className="mt-4 rounded-lg text-xs">
                {error}
              </Note>
            )}

            <Button
              type="submit"
              block
              disabled={code.length !== RULES.otp.length || submitting}
              className={cn(primaryBtn, 'mt-4')}
            >
              {submitting ? 'Verifying…' : 'Verify & get my pass'}
            </Button>

            <div className="mt-2 text-center">
              {cooldown > 0 ? (
                <span className="text-[13px] text-slate-500">Resend code in {mmss(cooldown)}</span>
              ) : (
                <Button type="button" variant="link" onClick={resend} className={linkBtn}>
                  Resend code
                </Button>
              )}
            </div>

            {!pending.accountExists && (
              <div className="text-center">
                <Button
                  type="button"
                  variant="link"
                  onClick={() => setChangingEmail(true)}
                  className={linkBtn}
                >
                  Wrong e-mail? Change it
                </Button>
              </div>
            )}
          </form>

          <Note className="mt-4 rounded-lg border-blue-100 bg-blue-50/80 text-xs text-slate-600">
            Your QR pass is issued only after your e-mail is verified, because your certificate will
            be sent to this address.
          </Note>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
